import { Cog6ToothIcon } from '@heroicons/react/20/solid';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useConst } from '@execonline-inc/react-hooks.private';
import { findLink } from '../../../../../LinkyLinky';
import { T } from '../../../../../Translations';
import Nav from '../../Nav';
import AdvancedSettingsModal from './AdvancedSettingsModal';
import Banner from './Banner';
import Breadcrumbs from './Breadcrumbs';
import CopyCollectionLinkButton from './CopyCollectionLinkButton';
import CreateShareUrlStore from './CreateShareUrlStore';
import CreateShareUrlReactions from './CreateShareUrlStore/Reactions';
import LinksTable from './LinksTable';
import PublishingReactions from './Publish/Reactions';
import PublishingStore from './Publish/Store';
import { emptyFragment } from '../../../../../EmptyFragment';
import { Link } from '../../../../../Resource/Types';
import DeepLinksOptionsStore from './DeepLinksOptionsStore';
import DeepLinksOptionsReactions from './DeepLinksOptionsStore/Reactions';
import Filters from './Filters';
import ShareableLinksStore from './Store';
import { Ready } from './Types';

interface Props {
  pageStore: ShareableLinksStore;
  state: Ready;
}

function Body({ pageStore, state }: Props) {
  const learningCollectionResource = state.resource;
  const { payload } = learningCollectionResource;

  const store = useConst(() => new CreateShareUrlStore(learningCollectionResource));
  const publishingStore = useConst(
    () =>
      new PublishingStore(
        findLink('publish', learningCollectionResource.links),
        learningCollectionResource,
      ),
  );
  const handleDownload = (link: Link) => {
    // Download in background using fetch to avoid loading spinner
    // credentials: 'include' ensures authentication cookies are sent
    fetch(link.href, { credentials: 'include' })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'course-links.csv';
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
  };
  const deepLinksOptionsStore = useConst(
    () => new DeepLinksOptionsStore(learningCollectionResource.links),
  );

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    deepLinksOptionsStore.loading();
  }, [deepLinksOptionsStore]);

  return (
    <>
      <div className="bg-card mx-auto max-w-clientPortalMaxWidth px-2.5 pt-2.5 lg:px-12 lg:pt-4 border-y-1">
        <Breadcrumbs name={payload.name} />
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-foreground">
            <T kind="Manage Participant Access" />
          </h2>
          <Nav id={payload.id} />
        </div>
      </div>
      <Banner learningCollection={payload} store={store} publishing={publishingStore.publishing} />
      <div
        className="max-w-clientPortalMaxWidth mx-auto px-2.5 lg:px-12 lg:my-4 pb-4 pt-8 flex justify-between items-center"
        data-intercom-target="shareable-links-header"
      >
        <h3 className="text-xl font-semibold text-foreground">
          <T kind="Shareable Links" />
        </h3>
        <button
          onClick={() => setIsShareModalOpen(true)}
          className="flex items-center text-primary hover:text-primary/90 text-sm cursor-pointer"
          data-intercom-target="shareable-links-advanced-settings-link"
        >
          <Cog6ToothIcon className="h-5 w-5 mr-1" />
          <T kind="Advanced Settings" />
        </button>
      </div>
      <Filters
        duplicateProgramDeepLinkCount={payload.duplicateProgramDeepLinkCount}
        optionsStore={deepLinksOptionsStore}
        searchParameters={state.searchParameters}
        updatingSearchParameters={pageStore.updatingSearchParameters}
        actionButtons={
          <>
            <CopyCollectionLinkButton learningCollectionResource={learningCollectionResource} />
            {findLink('program-deep-link-import', learningCollectionResource.links)
              .map((link) => (
                <button
                  onClick={() => handleDownload(link)}
                  className="rounded-md bg-primary px-3 py-2 text-primary-foreground text-sm"
                  data-intercom-target="shareable-links-download-all-button"
                >
                  <T kind="Download All Course Links" />
                </button>
              ))
              .getOrElse(emptyFragment)}
          </>
        }
      />
      <LinksTable programFamilyShareUrls={payload.programFamilyShareUrls} store={store} />
      <CreateShareUrlReactions
        fireImmediately
        store={store}
        pageStore={pageStore}
        learningCollectionResource={learningCollectionResource}
      />
      <PublishingReactions
        store={publishingStore}
        learningCollectionResource={learningCollectionResource}
        pageStore={pageStore}
      />
      <DeepLinksOptionsReactions store={deepLinksOptionsStore} fireImmediately={true} />
      {isShareModalOpen && (
        <AdvancedSettingsModal close={() => setIsShareModalOpen(false)} collectionId={payload.id} />
      )}
    </>
  );
}

export default observer(Body);
